import { computed, inject, Injectable, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { PlayerSession } from '@mos/api/authentication/dto-client'
import { Player, PlayerNotifications, PlayerSessionType, TutorialType } from '@mos/api/player/dto-client'
import { signalPersistence } from '@mos/client/shared/storage/signal-persistence'
import { StorageStrategy } from '@mos/client/shared/storage/strategies/storage.strategy'
import { preventPrerendering } from '@mos/client/shared/utils'

interface AuthProps {
	player: Player | null
	token: string | null
}

interface PersistedAuthProps {
	token: string | null
}

@Injectable({ providedIn: 'root' })
export class AuthRepository {
	storageStrategy = inject(StorageStrategy)
	private readonly authState = signal<AuthProps>({ player: null, token: null })

	constructor() {
		signalPersistence<PersistedAuthProps>({
			key: 'mos-auth',
			storage: this.storageStrategy,
			read: () => ({ token: this.authState().token }),
			write: (stored) => this.authState.update((state) => ({ ...state, token: stored.token })),
		})
	}

	get state(): AuthProps {
		return this.authState()
	}

	get player(): Player | null {
		return this.authState().player
	}

	token$ = toObservable(computed(() => this.authState().token))

	player$ = toObservable(computed(() => this.authState().player))

	playerTickets$ = toObservable(computed(() => this.authState().player?.currencies.tickets))

	playerMemoryKeys$ = toObservable(computed(() => this.authState().player?.currencies.memoryKeys))

	playerNotifications$ = toObservable(computed(() => this.authState().player?.notifications))

	setPlayerSession(session: PlayerSession) {
		if (session.type === PlayerSessionType.PREREGISTRATION) {
			return
		}

		preventPrerendering()
		this.authState.set({ player: session.player, token: session.token })
	}

	setPlayer(player: Player) {
		preventPrerendering()
		this.authState.update((state) => ({ ...state, player }))
	}

	updatePlayerCurrencyTicket(amount: number) {
		this.authState.update((state) => {
			if (!state.player) return state
			return {
				...state,
				player: { ...state.player, currencies: { ...state.player.currencies, tickets: amount } },
			}
		})
	}

	consumePlayerFreeOffer() {
		this.authState.update((state) => {
			if (!state.player || state.player.currencies.bankFreeTicketsCredited) return state
			return {
				...state,
				player: { ...state.player, currencies: { ...state.player.currencies, bankFreeTicketsCredited: true } },
			}
		})
	}

	updatePlayerCurrencyMemoryKeys(amount: number) {
		this.authState.update((state) => {
			if (!state.player) return state
			return {
				...state,
				player: { ...state.player, currencies: { ...state.player.currencies, memoryKeys: amount } },
			}
		})
	}

	updatePlayerNotifications(notifications: PlayerNotifications) {
		this.authState.update((state) => {
			if (!state.player) return state
			return { ...state, player: { ...state.player, notifications } }
		})
	}

	endPlayerTutorial(tutorialId: TutorialType) {
		this.authState.update((state) => {
			if (!state.player) return state
			return { ...state, player: { ...state.player, tutorials: [...(state.player.tutorials ?? []), tutorialId] } }
		})
	}

	removePlayer() {
		preventPrerendering(false)
		this.authState.set({ player: null, token: null })
	}
}
